import LegalLayout, { LegalSection } from "../components/LegalLayout";

export default function TermsPage() {
  return (
    <LegalLayout title="Terms of Service" updated="July 3, 2026" testid="terms-page">
      <p>
        Welcome to <strong>Evervow</strong>. These Terms of Service ("Terms") govern your use of the
        Evervow website, apps, and related services (the "Service"). By creating an account or using
        the Service, you agree to these Terms. If you do not agree, do not use the Service.
      </p>

      <LegalSection heading="1. Who can use Evervow">
        <ul className="list-disc pl-6 space-y-2">
          <li>You must be at least <strong>18 years old</strong> and legally able to enter a contract.</li>
          <li>You must not have been previously banned from the Service.</li>
          <li>You must not be required to be listed on any sex-offender registry.</li>
          <li>You agree to provide accurate information and to keep only one account per person.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="2. Your account">
        <p>
          You are responsible for the activity on your account and for keeping your login
          credentials confidential. Notify us at{" "}
          <a href="mailto:support@evervow.me" className="text-[#7A3B3F] underline underline-offset-4">
            support@evervow.me
          </a>{" "}
          immediately if you suspect unauthorized use.
        </p>
      </LegalSection>

      <LegalSection heading="3. Community & Safety Policy">
        <p>Evervow exists to help people build serious relationships. To keep it safe and respectful, the following are strictly prohibited:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Harassment, hate speech, or threats</strong> of any kind toward other members.</li>
          <li>
            <strong>Nudity or sexually explicit content</strong> in profile photos, bios, or messages.
            Evervow is a marriage-minded platform, not an adult service.
          </li>
          <li>
            <strong>Minors:</strong> any content sexualizing or depicting people under 18 is
            forbidden and will be reported to the appropriate authorities.
          </li>
          <li>
            <strong>Impersonation, catfishing, or fake profiles</strong>, including photos that are
            not of you.
          </li>
          <li>
            <strong>Solicitation:</strong> prostitution, escorting, or requesting/offering money,
            gifts, or financial support in exchange for interaction.
          </li>
          <li>
            <strong>Scams, spam, or promotional content</strong> — including crypto pitches, MLM
            recruiting, or off-platform advertising.
          </li>
          <li>
            <strong>Illegal activity</strong>, including the sale of drugs, weapons, or stolen items.
          </li>
          <li>
            <strong>Sharing others' private information</strong> (doxxing) or non-consensual intimate
            imagery.
          </li>
          <li>
            <strong>Automated access</strong> (scraping, bots, or unauthorized API use) or attempts
            to interfere with the Service.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="4. User content & moderation">
        <p>
          You retain ownership of the content (photos, bio, messages) you post to Evervow. By
          posting content, you grant Evervow a worldwide, non-exclusive license to host, display,
          and transmit that content solely to operate the Service.
        </p>
        <p>
          We may review, moderate, blur, remove, or refuse any content that violates these Terms or
          our Community & Safety Policy, with or without notice, and we may use automated systems to
          assist moderation.
        </p>
      </LegalSection>

      <LegalSection heading="5. Reporting & enforcement">
        <p>
          If you encounter behavior or content that violates these Terms, report it to{" "}
          <a href="mailto:support@evervow.me" className="text-[#7A3B3F] underline underline-offset-4">
            support@evervow.me
          </a>{" "}
          with the profile name and, if possible, a screenshot. We take reports seriously and will
          investigate promptly.
        </p>
        <p>Depending on the severity, our response may include:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Removing the offending content.</li>
          <li>Issuing a warning.</li>
          <li>Temporarily suspending the account.</li>
          <li>Permanently banning the account and, where appropriate, notifying law enforcement.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="6. Premium subscriptions">
        <p>
          Evervow Premium is billed as a straight one-time payment (monthly, quarterly, or yearly)
          via our payment processor Stripe. Payments are not auto-renewing; your Premium status
          simply extends by the plan length each time you purchase. All fees are non-refundable
          except where required by law.
        </p>
      </LegalSection>

      <LegalSection heading="7. Termination">
        <p>
          You may delete your account at any time — see{" "}
          <a href="/delete-account" className="text-[#7A3B3F] underline underline-offset-4">
            Delete your account
          </a>
          . We may suspend or terminate your access to the Service at any time if you violate these
          Terms, if we reasonably believe you pose a safety risk to other members, or if required by
          law.
        </p>
      </LegalSection>

      <LegalSection heading="8. Disclaimer of warranties">
        <p>
          The Service is provided "as is" and "as available." We do not guarantee that you will find
          a match, that any member's information is accurate, or that the Service will be
          uninterrupted or error-free. To the fullest extent permitted by law, Evervow disclaims all
          warranties, express or implied.
        </p>
      </LegalSection>

      <LegalSection heading="9. Limitation of liability">
        <p>
          To the fullest extent permitted by law, Evervow will not be liable for any indirect,
          incidental, consequential, or punitive damages, or for lost profits or data, arising out
          of your use of the Service. Our total liability for any claim will not exceed the amount
          you paid us in the 12 months preceding the claim.
        </p>
      </LegalSection>

      <LegalSection heading="10. Changes to these Terms">
        <p>
          We may update these Terms from time to time. If we make material changes, we will update
          the "Last updated" date above and, where appropriate, notify you in the app.
        </p>
      </LegalSection>

      <LegalSection heading="11. Contact">
        <p>
          Questions about these Terms? Email{" "}
          <a href="mailto:support@evervow.me" className="text-[#7A3B3F] underline underline-offset-4">
            support@evervow.me
          </a>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
